import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { Send, AlertCircle, User2, Smile, Frown, Meh } from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import {
  cn,
  formatRelative,
  priorityColor,
  priorityLabel,
  sentimentLabel,
  temperatureColor,
  temperatureLabel,
} from '../lib/utils.js';

export function Inbox(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id');
  const [filter, setFilter] = useState<'all' | 'open' | 'urgent'>('open');

  const { data: list, isLoading } = useQuery({
    queryKey: ['conversations', filter],
    queryFn: () =>
      apiClient.conversations({
        status: filter === 'all' ? undefined : filter === 'urgent' ? 'open' : 'open',
        priority: filter === 'urgent' ? 'urgent' : undefined,
        limit: 100,
      }),
    refetchInterval: 30_000,
  });

  // Auto-selecionar primeira conversa se nenhuma escolhida
  useEffect(() => {
    if (!selectedId && list?.conversations.length) {
      setSearchParams({ id: list.conversations[0].id });
    }
  }, [list, selectedId, setSearchParams]);

  return (
    <div className="flex h-screen">
      {/* Lista de conversas */}
      <aside className="w-96 shrink-0 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <h1 className="text-lg font-bold mb-3">Inbox</h1>
          <div className="flex gap-1">
            <FilterChip active={filter === 'open'} onClick={() => setFilter('open')}>Abertas</FilterChip>
            <FilterChip active={filter === 'urgent'} onClick={() => setFilter('urgent')}>Urgentes</FilterChip>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>Todas</FilterChip>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Carregando...</div>
          ) : list?.conversations.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Nenhuma conversa.</div>
          ) : (
            list?.conversations.map((c) => {
              const isActive = c.id === selectedId;
              const sent = sentimentLabel(c.sentimentScore);
              return (
                <button
                  key={c.id}
                  onClick={() => setSearchParams({ id: c.id })}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-border hover:bg-muted/30 transition',
                    isActive && 'bg-muted/50',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm truncate flex-1">{c.contactName ?? 'Sem nome'}</div>
                    <span className="text-[10px] text-muted-foreground shrink-0">{formatRelative(c.updatedAt)}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground line-clamp-1">
                    {c.lastMessage?.contentText ?? '—'}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    <Badge className={cn(priorityColor[c.priority] ?? '', 'text-[10px]')}>
                      {priorityLabel[c.priority]}
                    </Badge>
                    {c.contactTemperature && (
                      <Badge className={cn(temperatureColor[c.contactTemperature] ?? '', 'text-[10px]')}>
                        {temperatureLabel[c.contactTemperature]}
                      </Badge>
                    )}
                    <span className={cn('text-[10px]', sent.color)}>{sent.emoji}</span>
                    {Number(c.messageCount) > 0 && (
                      <span className="ml-auto text-[10px] text-muted-foreground">{c.messageCount} msg</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* Conversa selecionada */}
      <main className="flex-1 flex flex-col bg-background">
        {selectedId ? (
          <ConversationView id={selectedId} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            Selecione uma conversa
          </div>
        )}
      </main>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-md text-xs font-medium transition',
        active ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}

function ConversationView({ id }: { id: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => apiClient.conversationMessages(id),
    refetchInterval: 10_000,
    retry: false,
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => apiClient.sendMessage(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      setText('');
    },
  });

  // Cérebro do Ronaldo (modo Shadow): sugere a resposta e preenche o campo p/ revisão.
  const suggestMutation = useMutation({
    mutationFn: () => apiClient.aiSuggest(id),
    onSuccess: (r) => {
      if (r.suggestion) setText(r.suggestion);
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages.length]);

  if (isError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground text-sm gap-1 px-6 text-center">
        <span>Ainda nao ha conversa com este contato.</span>
        <span className="text-xs opacity-70">A isca de recuperacao foi enviada — quando o cliente responder, o papo aparece aqui.</span>
      </div>
    );
  }
  if (isLoading || !data) {
    return <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Carregando...</div>;
  }

  const { conversation, messages } = data;
  const sent = sentimentLabel(conversation.sentimentScore);

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">{conversation.contactName ?? 'Sem nome'}</h2>
            <Badge className={cn(priorityColor[conversation.priority] ?? '')}>
              {priorityLabel[conversation.priority]}
            </Badge>
            <span className={sent.color}>{sent.emoji} {sent.label}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {conversation.contactEmail} · {conversation.contactPhone} · {conversation.contactCity}
            {conversation.intentClassification && (
              <> · Intent: <span className="text-foreground">{conversation.intentClassification}</span></>
            )}
          </div>
        </div>
        <Link
          to={`/contatos/${conversation.contactId}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <User2 className="h-3.5 w-3.5" /> Ver 360
        </Link>
      </div>

      {/* Mensagens */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">Nenhuma mensagem.</div>
        ) : (
          messages.map((m) => {
            const isInbound = m.direction === 'inbound';
            return (
              <div key={m.id} className={cn('flex', isInbound ? 'justify-start' : 'justify-end')}>
                <Card className={cn('max-w-[70%]', isInbound ? '' : 'bg-primary/10 border-primary/30')}>
                  <div className="px-3 py-2">
                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                      {isInbound ? (
                        <>
                          <span>{conversation.contactName}</span>
                          {m.sentimentScore !== null && m.sentimentScore !== undefined && Number(m.sentimentScore) < -0.3 && (
                            <Frown className="h-3 w-3 text-red-400" />
                          )}
                          {m.sentimentScore !== null && m.sentimentScore !== undefined && Number(m.sentimentScore) > 0.3 && (
                            <Smile className="h-3 w-3 text-emerald-400" />
                          )}
                        </>
                      ) : (
                        <span>{m.senderName ?? 'Patricia'}</span>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{m.contentText}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 text-right">
                      {formatRelative(m.createdAt)}
                    </div>
                  </div>
                </Card>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-border p-3">
        <div className="mb-2 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={suggestMutation.isPending}
            onClick={() => suggestMutation.mutate()}
            className="text-xs"
          >
            🧠 {suggestMutation.isPending ? 'Pensando…' : 'Sugestão do Ronaldo (IA)'}
          </Button>
          {suggestMutation.data?.fallback && (
            <span className="text-[10px] text-amber-400">
              IA indisponível ({suggestMutation.data.fallbackReason ?? 'erro'}) — confira a chave/budget
            </span>
          )}
          {suggestMutation.data && !suggestMutation.data.fallback && (
            <span className="text-[10px] text-muted-foreground">
              sugestão pronta — revise e envie · {suggestMutation.data.basedOnMessages} msgs
            </span>
          )}
          {suggestMutation.isError && (
            <span className="text-[10px] text-red-400">erro ao sugerir</span>
          )}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim() && !sendMutation.isPending) {
              sendMutation.mutate(text.trim());
            }
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            placeholder="Digite uma mensagem..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={sendMutation.isPending}
            className="flex-1 h-10 rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
          />
          <Button type="submit" disabled={!text.trim() || sendMutation.isPending}>
            <Send className="h-4 w-4" />
            Enviar
          </Button>
        </form>
        {sendMutation.isError && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
            <AlertCircle className="h-3.5 w-3.5" /> Erro ao enviar: {(sendMutation.error as Error).message}
          </div>
        )}
        <div className="mt-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5">
          <Meh className="h-3 w-3" />
          Mock — integração WhatsApp Cloud API real entra na Fase 2 (broker híbrido)
        </div>
      </div>
    </>
  );
}
